import { IServerOrder } from '@rbi-ctg/menu';
import { ServiceMode } from 'generated/graphql-gateway';

type DeliveryAddressForReprice = {
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const normalizeAddressPart = (value?: string | null) => value?.trim().toLowerCase() ?? '';

/** Zip+4 and bare 5-digit should compare equal for settlement. */
const normalizeZip = (value?: string | null) => normalizeAddressPart(value).split('-')[0];

const secondaryAddressPartsMatch = (
  selected?: string | null,
  server?: string | null,
  normalize: (value?: string | null) => string = normalizeAddressPart
) => {
  const selectedPart = normalize(selected);
  const serverPart = normalize(server);
  // Missing secondary fields on either side are treated as wildcards so partial
  // server dropoffs cannot trap checkout in endless reprice loading.
  if (!selectedPart || !serverPart) {
    return true;
  }
  return selectedPart === serverPart;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The server's dropoff.addressLine1 is sometimes a single formatted string that
 * concatenates the street address with city/state/zip (e.g.
 * "4441 Collins Ave Miami Beach,FL 33140" or "4441 Collins Ave Miami Beach, FL 33140"),
 * while the client's addressLine1 is street-only ("4441 Collins Ave"). An exact match can
 * never succeed for this format, which permanently failed settlement and left checkout
 * loading forever (BKPE-27803).
 *
 * Strip the *known* city/state/zip suffix from the server's addressLine1 before comparing,
 * so only the actual street portion is checked. Built as a regex — rather than an exact
 * literal string, or a bidirectional startsWith that would also accept unrelated streets
 * sharing a prefix (e.g. "123 Main St" vs "123 Main St E ...") — so it tolerates whitespace
 * variance around the comma and an optional zip+4 suffix regardless of which side of the
 * comparison carries it.
 *
 * `state`/`zip` are IDeliveryWaypoint fields that are nullable in the schema (unlike the
 * required `addressLine1`/`city`), so the server dropoff can legitimately omit them even
 * when the selected address has them. When a state is known from EITHER side, it is
 * REQUIRED in the matched suffix, not merely tried — a street name that happens to end in
 * a word matching the city (e.g. a street literally named "...Miami" in the city of Miami)
 * must not have that word silently stripped just because a city value happens to be known,
 * whether or not the server's own state field happens to be populated.
 */
const stripKnownCityStateZipSuffix = (
  addressLine1: string,
  city?: string | null,
  state?: string | null,
  zip?: string | null
) => {
  const normalizedCity = normalizeAddressPart(city);
  const normalizedState = normalizeAddressPart(state);
  const normalizedZip = normalizeZip(zip);

  if (!normalizedCity) {
    return addressLine1;
  }

  // "<city>" followed by ",<state>" (comma, then optional whitespace before the state)
  // optionally followed by "<zip>" (whitespace, then the zip, tolerating a -NNNN suffix
  // regardless of whether the separate zip field itself carries one).
  const suffixPattern = normalizedState
    ? new RegExp(
        `\\s*${escapeRegExp(normalizedCity)}\\s*,\\s*${escapeRegExp(
          normalizedState
        )}(?:\\s+${escapeRegExp(normalizedZip)}(?:-\\d+)?)?$`
      )
    : new RegExp(`\\s*${escapeRegExp(normalizedCity)}$`);

  return addressLine1.replace(suffixPattern, '').trim();
};

const streetAddressMatches = (
  selected?: string | null,
  server?: string | null,
  serverCity?: string | null,
  serverState?: string | null,
  serverZip?: string | null,
  selectedState?: string | null,
  selectedZip?: string | null
) => {
  const selectedPart = normalizeAddressPart(selected);
  const serverPart = normalizeAddressPart(server);

  if (!selectedPart || !serverPart) {
    return false;
  }

  if (selectedPart === serverPart) {
    return true;
  }

  // A state known on either side is enough to require the fuller suffix — the server's
  // own `state` field being null must not alone regress to permissive city-only stripping
  // when we do have a state to compare against from the selected address.
  const knownState = normalizeAddressPart(serverState) ? serverState : selectedState;
  const knownZip = normalizeAddressPart(serverZip) ? serverZip : selectedZip;

  const serverStreetOnly = stripKnownCityStateZipSuffix(
    serverPart,
    serverCity,
    knownState,
    knownZip
  );

  return selectedPart === serverStreetOnly;
};

export const deliveryAddressesMatch = (
  selectedAddress?: DeliveryAddressForReprice | null,
  serverDropoff?: DeliveryAddressForReprice | null
) => {
  if (!selectedAddress || !serverDropoff) {
    return false;
  }

  if (
    !streetAddressMatches(
      selectedAddress.addressLine1,
      serverDropoff.addressLine1,
      serverDropoff.city,
      serverDropoff.state,
      serverDropoff.zip,
      selectedAddress.state,
      selectedAddress.zip
    )
  ) {
    return false;
  }

  return (
    secondaryAddressPartsMatch(selectedAddress.city, serverDropoff.city) &&
    secondaryAddressPartsMatch(selectedAddress.state, serverDropoff.state) &&
    secondaryAddressPartsMatch(selectedAddress.zip, serverDropoff.zip, normalizeZip)
  );
};

export const isDeliveryQuoteOrAddressStale = ({
  quoteId,
  serverOrderDeliveryQuoteId,
  deliveryAddress,
  serverOrderDropoff,
}: {
  quoteId?: string | null;
  serverOrderDeliveryQuoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrderDropoff?: DeliveryAddressForReprice | null;
}) => {
  // Prefer dropoff match over quoteId: after an address change, local quoteId often
  // lags the priced server quote even when delivery is already settled. Treating that
  // as pending is what kept Checkout V2 in endless loading (BKPE-27803 follow-up).
  if (deliveryAddressesMatch(deliveryAddress, serverOrderDropoff)) {
    return false;
  }

  if (quoteId && serverOrderDeliveryQuoteId) {
    return quoteId !== serverOrderDeliveryQuoteId;
  }

  return true;
};

export const getServerOrderStoreNumber = (serverOrder?: IServerOrder | null) =>
  serverOrder?.cart?.storeDetails?.storeNumber ?? serverOrder?.cart?.storeId?.toString?.() ?? null;

export const isCheckoutStoreRepriceStale = ({
  selectedStoreNumber,
  serverOrder,
}: {
  selectedStoreNumber?: string | null;
  serverOrder?: IServerOrder | null;
}) => {
  const serverOrderStoreNumber = getServerOrderStoreNumber(serverOrder);

  return Boolean(
    selectedStoreNumber && serverOrderStoreNumber && selectedStoreNumber !== serverOrderStoreNumber
  );
};

export const shouldMarkCheckoutRepriceRequestCompleted = ({
  checkoutRepriceRequestId,
  checkoutRepriceRequestCompletedId,
  serviceMode,
  quoteId,
  deliveryAddress,
  serverOrder,
  selectedStoreNumber,
}: {
  checkoutRepriceRequestId: number;
  checkoutRepriceRequestCompletedId: number;
  serviceMode?: ServiceMode | string | null;
  quoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrder?: IServerOrder | null;
  selectedStoreNumber?: string | null;
}) => {
  if (!checkoutRepriceRequestId || checkoutRepriceRequestId <= checkoutRepriceRequestCompletedId) {
    return false;
  }

  if (isCheckoutStoreRepriceStale({ selectedStoreNumber, serverOrder })) {
    return false;
  }

  if (
    serviceMode === ServiceMode.DELIVERY &&
    isDeliveryQuoteOrAddressStale({
      quoteId,
      serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
      deliveryAddress,
      serverOrderDropoff: serverOrder?.delivery?.dropoff,
    })
  ) {
    return false;
  }

  return true;
};

/**
 * Whether a priced server order may settle a specific reprice request for the
 * user's *current* store/delivery selection.
 *
 * Settlement prefers dropoff/store match so a newer quote for the same address
 * can sync local quoteId. A late success for an older address/store must not
 * advance completion for a newer reprice request.
 */
export const shouldSettleCheckoutRepriceFromPricedOrder = ({
  checkoutRepriceRequestId,
  checkoutRepriceRequestCompletedId,
  serviceMode,
  quoteId,
  deliveryAddress,
  serverOrder,
  selectedStoreNumber,
}: {
  checkoutRepriceRequestId: number;
  checkoutRepriceRequestCompletedId: number;
  serviceMode?: ServiceMode | string | null;
  quoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrder?: IServerOrder | null;
  selectedStoreNumber?: string | null;
}) => {
  if (!checkoutRepriceRequestId || checkoutRepriceRequestId <= checkoutRepriceRequestCompletedId) {
    return false;
  }

  if (isCheckoutStoreRepriceStale({ selectedStoreNumber, serverOrder })) {
    return false;
  }

  if (serviceMode !== ServiceMode.DELIVERY) {
    return true;
  }

  const serverDropoff = serverOrder?.delivery?.dropoff;
  if (deliveryAddress && serverDropoff) {
    return deliveryAddressesMatch(deliveryAddress, serverDropoff);
  }

  return !isDeliveryQuoteOrAddressStale({
    quoteId,
    serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
    deliveryAddress,
    serverOrderDropoff: serverDropoff,
  });
};
